import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import {
  LayoutDashboard,
  Building2,
  FileText,
  Package,
  Users,
  TrendingUp,
  Eye,
  MousePointer,
  Target,
  ArrowRight,
  Plus,
  CheckCircle2,
  Circle,
  Sparkles,
  Image,
  Code,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CoOpBalanceCard from "@/components/dashboard/CoOpBalanceCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function DashboardOverview() {
  const { user, organization, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, listingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/listings/my`, { headers: getAuthHeader() }),
      ]);
      setStats(statsRes.data);
      setListing(listingRes.data);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Profile completeness calculation
  const getProfileCompleteness = () => {
    if (!listing) return { percentage: 0, items: [] };
    
    const checks = [
      { label: "Add business name", done: !!listing.name, path: "/dashboard/listing" },
      { label: "Upload logo", done: !!listing.logo_url || listing.use_initials, path: "/dashboard/listing" },
      { label: "Add description", done: !!listing.description_long, path: "/dashboard/listing" },
      { label: "Add contact info", done: !!listing.phone_primary && !!listing.email, path: "/dashboard/listing" },
      { label: "Upload images", done: listing.gallery_images?.length > 0, path: "/dashboard/media" },
      { label: "Add social links", done: Object.values(listing.social_links || {}).some(v => v), path: "/dashboard/listing" },
      { label: "Publish first content", done: (stats?.content_count || 0) > 0, path: "/dashboard/content" },
      { label: "Add inventory", done: (stats?.inventory_count || 0) > 0, path: "/dashboard/inventory" },
    ];
    
    const completed = checks.filter(c => c.done).length;
    return {
      percentage: Math.round((completed / checks.length) * 100),
      items: checks,
    };
  };

  const completeness = getProfileCompleteness();

  const statCards = [
    { label: "Total Views", value: stats?.views || 0, icon: Eye, color: "#7C3AED", change: "+12%" },
    { label: "Leads", value: stats?.leads || 0, icon: Users, color: "#10B981", change: "+8%" },
    { label: "Clicks", value: stats?.clicks || 0, icon: MousePointer, color: "#F59E0B", change: "+15%" },
    { label: "Conversion Rate", value: `${stats?.conversion_rate || 0}%`, icon: Target, color: "#EC4899", change: "+2.1%" },
  ];

  const quickActions = [
    { label: "Add Content", icon: FileText, path: "/dashboard/content", color: "#7C3AED" },
    { label: "Add Inventory", icon: Package, path: "/dashboard/inventory", color: "#10B981" },
    { label: "Upload Media", icon: Image, path: "/dashboard/media", color: "#F59E0B" },
    { label: "Manage Pixels", icon: Code, path: "/dashboard/pixels", color: "#EC4899" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            Welcome back, {user?.first_name || "there"}! 👋
          </h1>
          <p className="text-[#94A3B8] mt-1">
            Here's what's happening with your listing today.
          </p>
        </div>
        {listing?.slug ? (
          <a href={`/business/${listing.slug}`} target="_blank" rel="noopener noreferrer" data-testid="preview-listing-overview-btn">
            <Button className="bg-[#7C3AED] hover:bg-[#8B5CF6]">
              <Eye className="w-4 h-4 mr-2" />
              Preview My Listing
            </Button>
          </a>
        ) : (
          <Link to="/dashboard/listing">
            <Button className="bg-[#7C3AED] hover:bg-[#8B5CF6]">
              <Building2 className="w-4 h-4 mr-2" />
              Set Up Listing
            </Button>
          </Link>
        )}
      </div>

      {/* Co-Op Balance */}
      <CoOpBalanceCard />

      {/* Profile Completeness */}
      {completeness.percentage < 100 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#7C3AED]/20 to-[#EC4899]/20 border border-[#7C3AED]/30 rounded-2xl p-6"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#F59E0B]" />
                Complete Your Profile
              </h2>
              <p className="text-sm text-[#94A3B8] mt-1">
                Complete listings get 5x more visibility!
              </p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-white">{completeness.percentage}%</span>
              <p className="text-xs text-[#94A3B8]">Complete</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="h-2 bg-white/10 rounded-full mb-4 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${completeness.percentage}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] rounded-full"
            />
          </div>

          {/* Checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {completeness.items.filter(item => !item.done).slice(0, 4).map((item, i) => (
              <Link
                key={item.label || `ck-${i}`}
                to={item.path}
                className="flex items-center gap-2 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
              >
                <Circle className="w-4 h-4 text-[#64748B]" />
                <span className="text-sm text-[#94A3B8] group-hover:text-white transition-colors">
                  {item.label}
                </span>
                <ArrowRight className="w-4 h-4 text-[#64748B] ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-[#0F172A] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20` }}
              >
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              <span className="text-xs font-medium text-[#10B981] bg-[#10B981]/10 px-2 py-1 rounded">
                {stat.change}
              </span>
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-sm text-[#64748B]">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1 min-w-0 bg-[#0F172A] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {quickActions.map((action, i) => (
              <Link
                key={action.label || `qa-${i}`}
                to={action.path}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${action.color}20` }}
                >
                  <action.icon className="w-5 h-5" style={{ color: action.color }} />
                </div>
                <span className="text-white font-medium">{action.label}</span>
                <Plus className="w-4 h-4 text-[#64748B] ml-auto group-hover:text-white transition-colors" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2 min-w-0 bg-[#0F172A] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
            <Link to="/dashboard/activity" className="text-sm text-[#7C3AED] hover:text-[#8B5CF6]">
              View all
            </Link>
          </div>
          
          {stats?.recent_activity?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_activity.slice(0, 5).map((activity, i) => (
                <div key={activity.id || `act-${i}`} className="flex items-start gap-3 pb-4 border-b border-white/5 last:border-0 last:pb-0">
                  <div className="w-8 h-8 rounded-full bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-4 h-4 text-[#7C3AED]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm">{activity.message}</p>
                    <p className="text-xs text-[#64748B] mt-1">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
              <p className="text-[#94A3B8]">No recent activity yet</p>
              <p className="text-sm text-[#64748B] mt-1">
                Activity will show up here as users interact with your listing.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Performance Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Performance Overview</h3>
          <select className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
        
        {/* Placeholder for chart */}
        <div className="h-64 bg-white/5 rounded-lg flex items-center justify-center">
          <p className="text-[#64748B]">Performance chart coming soon</p>
        </div>
      </motion.div>
    </div>
  );
}
